import { useMemo } from "react";
import { GetWSMgr } from "@/Utils/StateLib";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { PlanSubmission, ReviewerStatus } from "@/Utils/Plan/PlanSubmisionsTypes";

interface SubmissionVisibilityResult {
  isReviewer: boolean;
  isSubmitter: boolean;
  hasActiveSubmission: boolean;
  getCurrentUserReviewStatus: () => ReviewerStatus | null;
}

export const useSubmissionVisibility = (
  submission: PlanSubmission | null = null
): SubmissionVisibilityResult => {
  const { planManager } = usePlanManagerContext();

  const targetSubmission: PlanSubmission | null =
    submission || planManager?.state?.GetPlanSubmission();

  return useMemo(() => {
    const wsMgr = GetWSMgr();
    if (!wsMgr || !targetSubmission || !planManager) {
      return {
        isReviewer: false,
        isSubmitter: false,
        hasActiveSubmission: false,
        getCurrentUserReviewStatus: () => null
      };
    }

    // Check if plan approvals are enabled
    const enablePlanApprovals = planManager?.state?.GetApprovalSettings()?.enablePlanApprovals;
    if (!enablePlanApprovals) {
      return {
        isReviewer: false,
        isSubmitter: false,
        hasActiveSubmission: false,
        getCurrentUserReviewStatus: () => null
      };
    }

    const currentUser = wsMgr.GetUserName();

    const isReviewer =
      targetSubmission.reviewers.some(reviewer => reviewer.username === currentUser) || false;

    const isSubmitter = targetSubmission.submission?.username === currentUser;

    const hasActiveSubmission = targetSubmission && !targetSubmission.withdrawn;

    // Get current user's review status
    const getCurrentUserReviewStatus = (): ReviewerStatus | null => {
      const reviewer = targetSubmission.reviewers.find(r => r.username === currentUser);
      return reviewer?.status || null;
    };

    return {
      isReviewer,
      isSubmitter,
      hasActiveSubmission,
      getCurrentUserReviewStatus
    };
  }, [targetSubmission, planManager]);
};
